/**
 * useObservation.js — Lightweight anonymous telemetry hook
 * 
 * Tracks route visits and interaction events.
 * Aggregate only. No PII. No identity tracking.
 * Batches events and sends every 30 seconds.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BATCH_INTERVAL = 30000; // 30 seconds

let routeBuffer = [];
let interactionBuffer = [];

function flush() {
  if (routeBuffer.length === 0 && interactionBuffer.length === 0) return;

  const payload = {
    route_events: [...routeBuffer],
    interaction_events: [...interactionBuffer],
  };
  routeBuffer = [];
  interactionBuffer = [];

  fetch(`${API_URL}/api/v1/observe/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {}); // Silent fail — non-critical
}

export function useObservation() {
  const location = useLocation();
  const enterTime = useRef(Date.now());
  const prevRoute = useRef(null);

  useEffect(() => {
    const interval = setInterval(flush, BATCH_INTERVAL);
    window.addEventListener('beforeunload', flush);
    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', flush);
      flush();
    };
  }, []);

  useEffect(() => {
    const now = Date.now();
    if (prevRoute.current) {
      routeBuffer.push({
        route: prevRoute.current,
        duration_ms: now - enterTime.current,
        referrer_route: null,
        viewport_width: window.innerWidth,
      });
    }
    prevRoute.current = location.pathname;
    enterTime.current = now;
  }, [location.pathname]);

  const trackInteraction = useCallback((elementId, action = 'click') => {
    interactionBuffer.push({
      route: window.location.pathname,
      element_id: elementId,
      action,
    });
  }, []);

  return { trackInteraction };
}
